/**
 * Simple text-based Agent Setup: plants, machines, agent machine set, ACL.
 * Part of the agent definition page — not a complex form.
 * Scheduling UI is shown when bound tools/slots (or seed code bootstrap) mark scheduling.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Button } from '@/components/ui/Button';
import { getAgentSetup, putAgentSetup, listVersionTools } from '@/services/aiStudioAgentService';
import { FIELD_CLASS, HINT_CLASS, LABEL_CLASS } from './constants';
import { isSchedulingCapable } from '@/utils/aiStudioAgentFamily';

interface AgentSetupPanelProps {
  agentId: string;
  /** Agent code — bootstrap for seed agents when tools are empty. */
  agentCode?: string;
  /** Current definition version — used to derive scheduling from bound tools. */
  versionId?: string;
  /** When true, textarea is read-only. */
  readOnly: boolean;
}

export const AgentSetupPanel: React.FC<AgentSetupPanelProps> = ({
  agentId,
  agentCode,
  versionId,
  readOnly,
}) => {
  const { t } = useLanguage();
  const [isSchedulingFamily, setIsSchedulingFamily] = useState(() =>
    isSchedulingCapable({ agentCode })
  );
  const [capabilityReady, setCapabilityReady] = useState(!versionId);
  const [setupText, setSetupText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState<string | null>(null);
  const [meta, setMeta] = useState<{
    version: number;
    aclCount: number;
    hasCommittedPlan: boolean;
    configured: boolean;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!versionId) {
      setIsSchedulingFamily(isSchedulingCapable({ agentCode }));
      setCapabilityReady(true);
      return;
    }
    setCapabilityReady(false);
    void (async () => {
      try {
        const tools = await listVersionTools(versionId);
        if (cancelled) return;
        setIsSchedulingFamily(
          isSchedulingCapable({
            handlerRefs: tools.map((tool) => tool.handlerRef),
            agentCode,
          })
        );
      } catch {
        if (!cancelled) {
          setIsSchedulingFamily(isSchedulingCapable({ agentCode }));
        }
      } finally {
        if (!cancelled) setCapabilityReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [versionId, agentCode]);

  const load = useCallback(async () => {
    if (!isSchedulingFamily) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getAgentSetup(agentId);
      setSetupText(data.setupText);
      setMeta({
        version: data.version,
        aclCount: data.aclCount,
        hasCommittedPlan: data.hasCommittedPlan,
        configured: data.configured,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('aiStudio.setup.loadError'));
    } finally {
      setLoading(false);
    }
  }, [agentId, isSchedulingFamily, t]);

  useEffect(() => {
    if (!capabilityReady) return;
    if (!isSchedulingFamily) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, isSchedulingFamily, capabilityReady]);

  if (!capabilityReady) {
    return <p className={HINT_CLASS}>{t('aiStudio.setup.loading')}</p>;
  }

  if (!isSchedulingFamily) {
    return (
      <div className="flex flex-col gap-2">
        <p className={HINT_CLASS}>{t('aiStudio.setup.genericHint')}</p>
        <p className="text-sm text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.setup.genericDetail')}
        </p>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedHint(null);
    try {
      const data = await putAgentSetup(agentId, setupText);
      setSetupText(data.setupText);
      setMeta({
        version: data.version,
        aclCount: data.aclCount,
        hasCommittedPlan: data.hasCommittedPlan,
        configured: data.configured,
      });
      setSavedHint(
        t('aiStudio.setup.savedHint')
          .replace('{aclCount}', String(data.aclCount))
          .replace('{version}', String(data.version))
      );
    } catch (err: unknown) {
      const axiosMsg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data
          ? String((err.response.data as { error: unknown }).error)
          : null;
      setError(axiosMsg || (err instanceof Error ? err.message : t('aiStudio.setup.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <label className={LABEL_CLASS} htmlFor="agent-setup-text">
        {t('aiStudio.setup.title')}
      </label>
      <p className={HINT_CLASS}>{t('aiStudio.setup.hint')}</p>
      {meta && !meta.configured && (
        <div
          role="status"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-3 py-2 text-sm text-[var(--color-error)]"
        >
          <p className="font-medium">{t('aiStudio.setup.notConfiguredTitle')}</p>
          <p className="mt-1">{t('aiStudio.setup.notConfiguredHint')}</p>
        </div>
      )}
      {loading ? (
        <p className={HINT_CLASS}>{t('aiStudio.setup.loading')}</p>
      ) : (
        <textarea
          id="agent-setup-text"
          className={`${FIELD_CLASS} font-mono text-sm`}
          rows={18}
          value={setupText}
          disabled={readOnly}
          spellCheck={false}
          onChange={(e) => setSetupText(e.target.value)}
        />
      )}
      {meta ? (
        <p className={HINT_CLASS}>
          {t('aiStudio.setup.meta')
            .replace('{version}', String(meta.version))
            .replace('{aclCount}', String(meta.aclCount))
            .replace(
              '{plan}',
              meta.hasCommittedPlan ? t('aiStudio.setup.hasPlan') : t('aiStudio.setup.noPlan')
            )}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-base text-[var(--color-error)]">
          {error}
        </p>
      ) : null}
      {savedHint ? <p className="text-base text-[var(--color-success)]">{savedHint}</p> : null}
      {!readOnly ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => void save()} disabled={saving || loading}>
            {saving ? t('aiStudio.setup.saving') : t('aiStudio.setup.save')}
          </Button>
          <Button type="button" variant="outline" onClick={() => void load()} disabled={loading}>
            {t('aiStudio.setup.reload')}
          </Button>
        </div>
      ) : (
        <p className={HINT_CLASS}>{t('aiStudio.setup.readOnlyHint')}</p>
      )}
    </div>
  );
};
